import { celepusApiFetch } from './celepusApiClient';

interface Device {
  uuid: string;
  name: string;
  platform: string;
  hardware_id: string | null;
  last_seen_at: string | null;
  created_at: string;
}

interface ObjectFolder {
  name: string;
  prefix: string;
  size: number;
  file_count: number;
}

interface ObjectFile {
  path: string;
  size: number;
  last_modified: string;
  // Present on a live listing (`listObjects`); absent, `version_id` present
  // instead, on a Time Machine listing (`listObjectsAsOf`) — that's the
  // exact version that was live at the reconstructed point in time.
  etag?: string;
  version_id?: string;
  // Cached at upload time from the browser File object (see uploadFile.ts)
  // — nil for files uploaded before this existed, or by a client that
  // doesn't send it (e.g. the native app, currently). Only ever the
  // *current* version's values — not tracked historically, so absent from
  // Time Machine listings even for files that do have a cached row.
  content_type?: string | null;
  local_modified_at?: string | null;
}

interface ObjectListing {
  folders: ObjectFolder[];
  files: ObjectFile[];
  next_continuation_token: string | null;
}

const listDevices = (): Promise<Device[]> => celepusApiFetch('/api/v1/devices');

// Soft-deletes the device server-side (celepus-api keeps it for 6 months
// before permanent purge) — if the real device is still active, its next
// API call 404s and it re-registers from scratch, triggering a genuine
// fresh full backup rather than silently resuming against a device_uuid
// that no longer exists.
const deleteDevice = (deviceUuid: string): Promise<void> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}`, { method: 'DELETE' });

// Every user has exactly one "web" pseudo-device for manual browser
// uploads/folders, distinct from real backup-source devices (which are
// read-only from here) — find-or-create it. Safe to call every time the
// user opens the web folder, not just once.
const getOrCreateWebFolder = (): Promise<Device> =>
  celepusApiFetch('/api/v1/web_folder', { method: 'POST' });

const listObjects = (deviceUuid: string, prefix?: string): Promise<ObjectListing> => {
  const query = prefix ? `?prefix=${encodeURIComponent(prefix)}` : '';
  return celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects${query}`);
};

// "Time Machine": reconstructs the listing as it looked at `asOf` (an ISO8601
// timestamp) instead of the live listing — same response shape as
// `listObjects`, minus `next_continuation_token` (server always returns the
// full reconstructed level in one response, no pagination for this path).
const listObjectsAsOf = (deviceUuid: string, prefix: string | undefined, asOf: Date): Promise<ObjectListing> => {
  const params = new URLSearchParams({ as_of: asOf.toISOString() });
  if (prefix) params.set('prefix', prefix);
  return celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects?${params.toString()}`);
};

interface SearchResults {
  folders: { name: string; prefix: string }[];
  files: { path: string; size: number; last_modified: string; content_type?: string | null; local_modified_at?: string | null }[];
  truncated: boolean;
}

// Case-insensitive substring match against each item's own name, scoped to
// `prefix` and everything beneath it — never the whole device, matching how
// this is surfaced in the UI ("search from where you currently are").
const searchObjects = (deviceUuid: string, prefix: string | undefined, query: string): Promise<SearchResults> => {
  const params = new URLSearchParams({ q: query });
  if (prefix) params.set('prefix', prefix);
  return celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/search?${params.toString()}`);
};

interface FolderTree {
  files: { path: string; size: number }[];
  truncated: boolean;
}

// Every real file recursively under `prefix`, paths relative to it —
// preparation for a client-side ZIP download (see FolderZip.ts): the server
// can't build the zip itself since every file is end-to-end encrypted, so
// the browser needs the full file list to then presign-download and
// decrypt each one itself.
const getFolderTree = (deviceUuid: string, prefix: string): Promise<FolderTree> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/tree?prefix=${encodeURIComponent(prefix)}`);

// Folders are otherwise purely virtual (derived from existing object key
// prefixes) — this PUTs a real zero-byte marker object so an empty folder
// still shows up in listings/survives navigating away and back.
const createFolder = (deviceUuid: string, path: string): Promise<{ path: string }> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/create_folder`, {
    method: 'POST',
    body: JSON.stringify({ path }),
  });

interface EncryptionMetadata {
  wrapped_dek: string;
  dek_nonce: string;
  chunk_nonce_prefix: string;
  chunk_size: string;
  original_content_type: string;
}

interface PresignedDownload {
  url: string;
  expires_at: string;
  encryption: EncryptionMetadata;
}

// `versionId` omitted downloads the current version (unchanged behavior);
// passed, downloads that specific past version (see `listFileVersions`) —
// independently decryptable, since every version carries its own
// encryption metadata from whenever it was originally uploaded.
const presignDownload = (deviceUuid: string, path: string, versionId?: string): Promise<PresignedDownload> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/presign_download`, {
    method: 'POST',
    body: JSON.stringify(versionId ? { path, version_id: versionId } : { path }),
  });

interface FileVersion {
  version_id: string;
  size: number;
  last_modified: string;
  is_latest: boolean;
}

const listFileVersions = (deviceUuid: string, path: string): Promise<{ versions: FileVersion[] }> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/versions?path=${encodeURIComponent(path)}`);

// Deletes one file. The bucket has versioning enabled (see
// hetzner-backup-infra's setup-backup-bucket.sh), so this adds a delete
// marker rather than destroying anything outright — the file stays
// recoverable (via its old version_id, or a Time Machine view from before
// the delete) until the noncurrent-version lifecycle rule eventually
// expires it. Restricted to the web folder in the UI — real devices stay
// read-only from here, same as new-folder/upload.
const deleteFile = (deviceUuid: string, path: string): Promise<void> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/destroy_file?path=${encodeURIComponent(path)}`, {
    method: 'DELETE',
  });

// Recursively deletes every file under a folder prefix (including the
// folder's own marker) — same delete-marker semantics as `deleteFile`.
const deleteFolder = (deviceUuid: string, prefix: string): Promise<void> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/destroy_folder?path=${encodeURIComponent(prefix)}`, {
    method: 'DELETE',
  });

interface UploadCryptoParams {
  wrapped_dek: string;
  dek_nonce: string;
  chunk_nonce_prefix: string;
  chunk_size: number;
  original_content_type: string;
  // The source file's own last-modified timestamp (an ISO8601 string,
  // converted from the browser File object's `lastModified` epoch-ms) —
  // distinct from when it was uploaded, which is just the S3 object's own
  // last_modified. Optional server-side (see celepus-api's
  // ObjectMetadataEntry) so this stays backward compatible with any other
  // client that doesn't send it.
  local_modified_at: string;
}

interface PresignedUpload {
  url: string;
  method: string;
  headers: Record<string, string>;
  expires_at: string;
}

const presignUpload = (
  deviceUuid: string,
  path: string,
  cryptoParams: UploadCryptoParams
): Promise<PresignedUpload> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/objects/presign_upload`, {
    method: 'POST',
    body: JSON.stringify({ path, ...cryptoParams }),
  });

interface MultipartUploadInit {
  uuid: string;
  part_size_hint: number;
}

const initiateMultipartUpload = (
  deviceUuid: string,
  path: string,
  cryptoParams: UploadCryptoParams
): Promise<MultipartUploadInit> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/uploads`, {
    method: 'POST',
    body: JSON.stringify({ path, ...cryptoParams }),
  });

interface PresignedPart {
  url: string;
  method: string;
  expires_at: string;
}

const presignPart = (deviceUuid: string, uploadUuid: string, partNumber: number): Promise<PresignedPart> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/uploads/${uploadUuid}/parts/${partNumber}/presign`, {
    method: 'POST',
  });

interface CompletedPart {
  part_number: number;
  etag: string;
}

const completeMultipartUpload = (
  deviceUuid: string,
  uploadUuid: string,
  parts: CompletedPart[]
): Promise<{ uuid: string; status: string }> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/uploads/${uploadUuid}/complete`, {
    method: 'POST',
    body: JSON.stringify({ parts }),
  });

const abortMultipartUpload = (deviceUuid: string, uploadUuid: string): Promise<void> =>
  celepusApiFetch(`/api/v1/devices/${deviceUuid}/uploads/${uploadUuid}`, { method: 'DELETE' });

export type {
  Device,
  ObjectFolder,
  ObjectFile,
  ObjectListing,
  EncryptionMetadata,
  PresignedDownload,
  UploadCryptoParams,
  PresignedUpload,
  MultipartUploadInit,
  PresignedPart,
  CompletedPart,
  FileVersion,
  SearchResults,
  FolderTree,
};

export {
  listDevices,
  deleteDevice,
  getOrCreateWebFolder,
  listObjects,
  listObjectsAsOf,
  searchObjects,
  getFolderTree,
  createFolder,
  presignDownload,
  listFileVersions,
  deleteFile,
  deleteFolder,
  presignUpload,
  initiateMultipartUpload,
  presignPart,
  completeMultipartUpload,
  abortMultipartUpload,
};
